import React, { useEffect, useState } from "react";

import { ChevronRight, Loader2, Wallet, Plus, Info, RefreshCw } from "lucide-react";

import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../components/ui/tooltip";
import { useAuth } from "../../../contexts/AuthContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { useCurrencyContext } from "../../../hooks/useCurrencyContext";
import { formatUsdDollars } from "../../../lib/currency";
import {
  cloudUsageApi,
  CloudBalanceWithUsage,
  formatBytes,
  formatNumber,
} from "../../../lib/api/cloud-usage";
import { cn } from "../../../lib/utils";

interface CloudBalanceCardProps {
  className?: string;
  onAddFunds: () => void;
}

// Meter display names
// Note: db_storage and file_storage removed - Supabase doesn't charge separately
const METER_LABELS: Record<string, string> = {
  edge_functions: "Edge Functions",
  r2_storage: "Assets Storage",
  app_traffic: "App Traffic",
  realtime_messages: "Realtime Messages",
  realtime_peak_connections: "Realtime Connections",
  instance: "Database Compute",
};

// Meter tier sizes for display
const METER_TIER_INFO: Record<string, { size: number; unit: string }> = {
  edge_functions: { size: 1000000, unit: "invocations" },
  r2_storage: { size: 1, unit: "GB" },
  app_traffic: { size: 1000000, unit: "requests" },
  realtime_messages: { size: 1000000, unit: "messages" },
  realtime_peak_connections: { size: 1000, unit: "connections" },
  instance: { size: 1, unit: "projects" },
};

interface MeterRowProps {
  meter: string;
  usage: number;
  tiersPurchased: number;
  cost: number;
  included?: number;
  displayCurrency: string;
  rates: Record<string, number>;
}

const MeterRow: React.FC<MeterRowProps> = ({ meter, usage, tiersPurchased, cost, included, displayCurrency, rates }) => {
  const [open, setOpen] = useState(false);
  const label = METER_LABELS[meter] || meter;
  const tierInfo = METER_TIER_INFO[meter];

  // For instance meter, capacity includes both included + purchased tiers
  const isInstanceMeter = meter === "instance";
  const totalCapacity = isInstanceMeter && included
    ? included + tiersPurchased
    : tiersPurchased * (tierInfo?.size || 1);
  const usagePercent = totalCapacity > 0 ? Math.min((usage / totalCapacity) * 100, 100) : 0;

  // Format usage display
  const formatUsage = (value: number): string => {
    if (meter.includes("storage")) {
      return formatBytes(value * 1024 * 1024 * 1024); // Convert GB to bytes for display
    }
    return formatNumber(value);
  };

  // Format capacity display
  const formatCapacity = (): string => {
    if (!tierInfo) return "";
    if (isInstanceMeter && included) {
      return `${totalCapacity} ${tierInfo.unit}`;
    }
    if (meter.includes("storage")) {
      return `${tiersPurchased} ${tierInfo.unit}`;
    }
    return `${formatNumber(tiersPurchased * tierInfo.size)} ${tierInfo.unit}`;
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between py-2.5 px-1 hover:bg-muted/50 rounded-md transition-colors cursor-pointer">
        <div className="flex items-center gap-1.5">
          <ChevronRight
            className={cn(
              "h-3.5 w-3.5 text-muted-foreground transition-transform duration-200",
              open && "rotate-90"
            )}
          />
          <span className="text-sm">{label}</span>
          {meter === "r2_storage" && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Info className="h-3.5 w-3.5 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">Workspace-level storage (not per-project)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <span className="text-sm font-medium tabular-nums">
          {formatUsdDollars(cost, displayCurrency, rates)}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="ml-5 py-2 pl-2 space-y-2">
          {/* Progress bar */}
          <div className="space-y-1">
            <Progress value={usagePercent} className="h-1.5 bg-foreground/20 [&>div]:bg-foreground" />
            <div className="flex justify-between text-xs text-muted-foreground">
              {isInstanceMeter && included ? (
                <>
                  <span>{usage} active / {included} included</span>
                  {totalCapacity > 0 && <span>{formatCapacity()} capacity</span>}
                </>
              ) : (
                <>
                  <span>{formatUsage(usage)} used</span>
                  {tiersPurchased > 0 && <span>{formatCapacity()} capacity</span>}
                </>
              )}
            </div>
          </div>
          {tiersPurchased > 0 && (
            <p className="text-xs text-muted-foreground">
              {tiersPurchased} tier{tiersPurchased > 1 ? "s" : ""} purchased this cycle
            </p>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const CloudBalanceCard: React.FC<CloudBalanceCardProps> = ({
  className,
  onAddFunds,
}) => {
  const { getIdToken } = useAuth();
  const { isAdminOrOwner } = usePermissions();
  const { context: currency } = useCurrencyContext();
  const [data, setData] = useState<CloudBalanceWithUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadBalance();
  }, []);

  const loadBalance = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError("Authentication required");
        return;
      }
      const result = await cloudUsageApi.getBalanceWithUsage(token);
      setData(result);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      console.error("Error loading cloud balance:", err);
      if (axiosErr.response?.status !== 404) {
        setError(axiosErr.response?.data?.message || "Failed to load balance data");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">{error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card className={className}>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">
            No balance data available.
          </p>
        </CardContent>
      </Card>
    );
  }

  const { balance, costs, totalCost } = data;
  const hasPlanAllowance = balance.allocated > 0;
  const planPercent = balance.totalBalance > 0 ? (balance.planBalance / balance.totalBalance) * 100 : 0;
  const fundsPercent = balance.totalBalance > 0 ? (balance.topupBalance / balance.totalBalance) * 100 : 0;
  const planUsedPercent = balance.allocated > 0
    ? Math.min((balance.planBalance / balance.allocated) * 100, 100)
    : 0;

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="p-0">
        {/* Header with Total Balance */}
        <div className="bg-gradient-to-br from-zinc-50 via-white to-zinc-50 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900 border-b border-zinc-100 dark:border-zinc-700 px-5 py-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-zinc-500 dark:text-zinc-400 text-xs font-medium tracking-wide uppercase">
              Total Cloud Balance
            </span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button className="text-zinc-400 hover:text-zinc-600 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors">
                    <Info className="h-3.5 w-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-[280px]">
                  <p className="text-xs">
                    Your cloud balance covers infrastructure costs like edge functions,
                    storage, and traffic.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="text-3xl font-bold text-zinc-900 dark:text-white tracking-tight tabular-nums">
            {formatUsdDollars(balance.totalBalance, currency.displayCurrency, currency.rates)}
          </div>

          {/* Stacked composition bar */}
          {balance.totalBalance > 0 && (
            <div className="mt-3 space-y-1.5">
              <div className="h-1.5 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden flex">
                {balance.planBalance > 0 && (
                  <div
                    className="h-full bg-blue-500 transition-all duration-500"
                    style={{ width: `${planPercent}%` }}
                  />
                )}
                {balance.topupBalance > 0 && (
                  <div
                    className="h-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${fundsPercent}%` }}
                  />
                )}
              </div>
              <div className="flex items-center gap-3 text-[11px]">
                {balance.planBalance > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    <span className="text-zinc-500 dark:text-zinc-400">Monthly</span>
                  </div>
                )}
                {balance.topupBalance > 0 && (
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    <span className="text-zinc-500 dark:text-zinc-400">Added Funds</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Two Balance Pools */}
        <div className="p-4 space-y-3">
          {/* Monthly Allowance Pool */}
          <div className="rounded-lg border border-blue-200 bg-blue-50/50 dark:border-blue-900/50 dark:bg-blue-950/20 p-3 transition-all">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md flex items-center justify-center bg-blue-100 dark:bg-blue-900/40">
                  <RefreshCw className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <h3 className="font-medium text-sm">Monthly Allowance</h3>
                    {balance.billingSource === 'embedded' && hasPlanAllowance && (
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="text-xs">
                              Allocated based on {balance.screenCount} screen{balance.screenCount !== 1 ? 's' : ''} with OptiDev Add-on
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {hasPlanAllowance
                      ? `Resets to ${formatUsdDollars(balance.allocated, currency.displayCurrency, currency.rates)} monthly`
                      : "Included with paid plans"}
                  </p>
                </div>
              </div>

              <div className="text-right">
                {hasPlanAllowance ? (
                  <div className="text-base font-semibold tabular-nums">
                    {formatUsdDollars(balance.planBalance, currency.displayCurrency, currency.rates)}
                  </div>
                ) : (
                  <div className="text-base font-medium text-muted-foreground">—</div>
                )}
              </div>
            </div>

            {/* Usage bar - only show when there's an allowance */}
            {hasPlanAllowance && (
              <div className="mt-2.5">
                <div className="h-1 rounded-full bg-blue-100 dark:bg-blue-900/40 overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all duration-500"
                    style={{ width: `${planUsedPercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Added Funds Pool */}
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 dark:border-emerald-900/50 dark:bg-emerald-950/20 p-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                  <Wallet className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <h3 className="font-medium text-sm">Added Funds</h3>
                  <p className="text-xs text-muted-foreground">Never expires</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="text-base font-semibold tabular-nums">
                  {formatUsdDollars(balance.topupBalance, currency.displayCurrency, currency.rates)}
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onAddFunds}
                  disabled={!isAdminOrOwner}
                  className="h-7 px-2.5 border-emerald-300 hover:bg-emerald-100 hover:border-emerald-400 dark:border-emerald-800 dark:hover:bg-emerald-900/40"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Usage Breakdown by Meter */}
        {costs.length > 0 && (
          <div className="px-4 pb-4 space-y-1 pt-2 border-t border-border/50">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>Usage This Cycle</span>
              <span className="font-medium">{formatUsdDollars(totalCost, currency.displayCurrency, currency.rates)}</span>
            </div>
            {costs.map((meterCost) => (
              <MeterRow
                key={meterCost.meter}
                meter={meterCost.meter}
                usage={meterCost.usage}
                tiersPurchased={meterCost.tiersPurchased}
                cost={meterCost.cost}
                included={meterCost.included}
                displayCurrency={currency.displayCurrency}
                rates={currency.rates}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
